import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, ChevronDown, Search, User, X } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";

/**
 * QENVA · Workspace Assignee Picker.
 *
 * Compact pill that opens a searchable popover listing workspace members.
 * Selecting a member calls `onAssign(user_id | null)`. Used in AlertDrawer
 * (one-click reassignment) and the "My Alerts" filter row.
 */
function Avatar({ name, email, size = 26 }) {
  const initials = (name || email || "?").trim()[0].toUpperCase();
  return (
    <span
      className="inline-flex items-center justify-center rounded-full bg-qenva-blue/15 text-qenva-blue font-bold shrink-0"
      style={{ width: size, height: size, fontSize: size * 0.42 }}
    >
      {initials}
    </span>
  );
}

export default function AssigneePicker({
  currentAssignee,        // { user_id, name, email, avatar_url } | null
  onAssign,
  size = "md",
  disabled = false,
  testId = "assignee-picker",
}) {
  const { t } = useTranslation("alerts");
  const [open, setOpen] = useState(false);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [pending, setPending] = useState(false);

  useEffect(() => {
    if (!open || members.length > 0) return;
    setLoading(true);
    api.get("/alm/members")
      .then((r) => setMembers(r.data?.items || []))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load members"))
      .finally(() => setLoading(false));
  }, [open, members.length]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return members;
    return members.filter((m) =>
      (m.name || "").toLowerCase().includes(q) ||
      (m.email || "").toLowerCase().includes(q),
    );
  }, [members, query]);

  const handleSelect = async (userId) => {
    setPending(true);
    try {
      await onAssign(userId);
      setOpen(false);
    } finally {
      setPending(false);
    }
  };

  const isCompact = size === "sm";
  const padding = isCompact ? "px-2.5 py-1" : "px-3 py-1.5";
  const textSize = isCompact ? "text-[11px]" : "text-[12.5px]";

  return (
    <div className="relative inline-block" data-testid={testId}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        data-testid={`${testId}-trigger`}
        key={currentAssignee?.user_id || "unassigned"}
        className={`inline-flex items-center gap-2 rounded-full border border-zinc-200 bg-white hover:border-qenva-blue/40 transition-colors ${padding} ${textSize} ${
          disabled ? "opacity-60 cursor-not-allowed" : ""
        }`}
      >
        {currentAssignee ? (
          <>
            <Avatar name={currentAssignee.name} email={currentAssignee.email} size={isCompact ? 18 : 22} />
            <span className="font-semibold text-qenva-navy truncate max-w-[160px]" title={currentAssignee.name || currentAssignee.email}>
              {currentAssignee.name || currentAssignee.email || "—"}
            </span>
          </>
        ) : (
          <>
            <span className={`inline-flex items-center justify-center rounded-full bg-zinc-100 text-zinc-400`} style={{ width: isCompact ? 18 : 22, height: isCompact ? 18 : 22 }}>
              <User size={isCompact ? 11 : 12} />
            </span>
            <span className="text-zinc-500">{t("assign.unassigned")}</span>
          </>
        )}
        <ChevronDown size={12} className="text-zinc-400" />
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 z-30 w-[280px] bg-white rounded-xl border border-zinc-200 shadow-lg overflow-hidden"
          data-testid={`${testId}-panel`}
        >
          <div className="px-3 py-2 border-b border-zinc-100">
            <div className="relative">
              <Search size={11} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t("assign.search_placeholder")}
                className="w-full pl-7 pr-2 py-1 rounded-lg border border-zinc-200 text-[12px] focus:outline-none focus:border-qenva-blue/60 focus:ring-2 focus:ring-qenva-blue/15"
                data-testid={`${testId}-search`}
                autoFocus
              />
            </div>
          </div>
          <ul className="max-h-64 overflow-y-auto" data-testid={`${testId}-list`}>
            <li>
              <button
                type="button"
                onClick={() => handleSelect(null)}
                disabled={pending}
                data-testid={`${testId}-unassign`}
                className={`w-full flex items-center gap-2 px-3 py-2 hover:bg-zinc-50 ${
                  !currentAssignee ? "bg-qenva-blue/5" : ""
                }`}
              >
                <span className="inline-flex items-center justify-center rounded-full bg-zinc-100 text-zinc-400 w-[22px] h-[22px]">
                  <X size={11} />
                </span>
                <span className="text-[12.5px] text-zinc-700 flex-1 text-left">{t("assign.unassigned")}</span>
                {!currentAssignee && <Check size={13} className="text-qenva-blue" />}
              </button>
            </li>
            {loading ? (
              <li className="px-3 py-3 text-[12px] text-zinc-400">{t("common.loading")}</li>
            ) : filtered.length === 0 ? (
              <li className="px-3 py-3 text-[12px] text-zinc-400">{t("assign.no_members")}</li>
            ) : filtered.map((m) => {
              const active = currentAssignee?.user_id === m.user_id;
              return (
                <li key={m.user_id}>
                  <button
                    type="button"
                    onClick={() => handleSelect(m.user_id)}
                    disabled={pending}
                    data-testid={`${testId}-member-${m.user_id}`}
                    className={`w-full flex items-center gap-2 px-3 py-2 hover:bg-zinc-50 ${
                      active ? "bg-qenva-blue/5" : ""
                    }`}
                  >
                    <Avatar name={m.name} email={m.email} size={22} />
                    <span className="flex-1 text-left min-w-0">
                      <span className="block text-[12.5px] text-qenva-navy font-semibold truncate">
                        {m.name || m.email}
                      </span>
                      <span className="block text-[10.5px] text-zinc-500 truncate">
                        {m.email} · {m.role}
                      </span>
                    </span>
                    {m.open_alert_count > 0 && (
                      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-50 text-amber-700">
                        {m.open_alert_count}
                      </span>
                    )}
                    {active && <Check size={13} className="text-qenva-blue ml-1 shrink-0" />}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
